import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { Lock, Unlock, Loader2, X, Plus } from "lucide-react";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const monthLabel = (p) => `${MONTH_NAMES[p.month - 1]} ${p.year}`;

/**
 * Settings card listing every month a tenant has frozen for close.
 * Owner/admin only — the surrounding Settings page already guards role.
 * Mirrors the Reconciliation page "Close month" shortcut: both call the
 * same endpoints so the two surfaces stay in sync without local caching.
 */
export default function LockedPeriodsCard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() + 1, note: "" };
  });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/locked-periods");
      setItems(r.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load closed periods");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/locked-periods", {
        year: Number(form.year), month: Number(form.month), note: form.note.trim() || undefined,
      });
      toast.success(`${monthLabel({ year: form.year, month: form.month })} closed`);
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Lock failed");
    } finally { setBusy(false); }
  };

  const unlock = async (p) => {
    if (!window.confirm(`Re-open ${monthLabel(p)}? Transactions in this month will become editable again. An audit entry is recorded.`)) return;
    try {
      await api.delete(`/locked-periods/${p.year}/${p.month}`);
      toast.success(`${monthLabel(p)} re-opened`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unlock failed");
    }
  };

  return (
    <div className="ll-card-flat p-6 mb-6" data-testid="locked-periods-card">
      <div className="flex items-start justify-between mb-4 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">Compliance</div>
          <div className="font-display font-bold text-2xl mt-1 flex items-center gap-2">
            <Lock size={18} /> Closed periods
          </div>
          <p className="text-sm text-[var(--ll-ink-soft)] mt-1 max-w-xl">
            Freeze a month after close so transactions dated inside it can&apos;t be edited or deleted by accident. Notes stay editable so follow-up flags still work.
          </p>
        </div>
        <button
          onClick={() => setOpen(true)}
          data-testid="lock-period-open"
          className="text-sm px-3 py-2 bg-[var(--ll-ink)] text-white hover:bg-[var(--ll-ink-hover)] flex items-center gap-1.5"
        >
          <Plus size={14} /> Close a month
        </button>
      </div>

      {loading ? (
        <div className="py-6 text-center text-sm text-[var(--ll-ink-soft)]">
          <Loader2 size={16} className="mx-auto animate-spin mb-2" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="py-6 text-center text-sm text-[var(--ll-ink-soft)]" data-testid="locked-periods-empty">
          No months are closed. Lock one once your accounting review is done.
        </div>
      ) : (
        <table className="w-full text-sm" data-testid="locked-periods-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-white bg-[var(--ll-bg-sidebar)] border-b" style={{ borderColor: "var(--ll-border)" }}>
              <th className="px-3 py-2 font-medium">Period</th>
              <th className="px-3 py-2 font-medium">Closed by</th>
              <th className="px-3 py-2 font-medium">Closed on</th>
              <th className="px-3 py-2 font-medium">Note</th>
              <th className="px-3 py-2 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-b" style={{ borderColor: "var(--ll-border)" }}>
                <td className="px-3 py-2 font-medium">{monthLabel(p)}</td>
                <td className="px-3 py-2 text-xs text-[var(--ll-ink-soft)]">{p.locked_by_name || "—"}</td>
                <td className="px-3 py-2 text-xs text-[var(--ll-ink-soft)]">{fmtDate(p.locked_at)}</td>
                <td className="px-3 py-2 text-xs">{p.note || <span className="opacity-50">—</span>}</td>
                <td className="px-3 py-2 text-right">
                  <button
                    onClick={() => unlock(p)}
                    data-testid={`unlock-period-${p.year}-${p.month}`}
                    className="text-[11px] px-2 py-1 border hover:bg-[var(--ll-mint)] flex items-center gap-1 ml-auto"
                    style={{ borderColor: "var(--ll-border)" }}
                  >
                    <Unlock size={11} /> Re-open
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setOpen(false)} data-testid="lock-period-modal">
          <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white border w-full max-w-md p-6" style={{ borderColor: "var(--ll-ink)" }}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h2 className="font-display font-bold text-xl flex items-center gap-2"><Lock size={16} /> Close a month</h2>
                <p className="text-xs text-[var(--ll-ink-soft)] mt-1">Prevents accidental edits to transactions dated inside this month.</p>
              </div>
              <button type="button" onClick={() => setOpen(false)} aria-label="Close" className="text-[var(--ll-ink-soft)]"><X size={16} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">Year</span>
                <input
                  type="number"
                  min={1900} max={2999}
                  value={form.year}
                  onChange={(e) => setForm({ ...form, year: e.target.value })}
                  data-testid="lock-period-year"
                  className="mt-1 w-full px-3 py-2 border text-sm font-mono-fin"
                  style={{ borderColor: "var(--ll-border)" }}
                />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">Month</span>
                <select
                  value={form.month}
                  onChange={(e) => setForm({ ...form, month: e.target.value })}
                  data-testid="lock-period-month"
                  className="mt-1 w-full px-3 py-2 border text-sm bg-white"
                  style={{ borderColor: "var(--ll-border)" }}
                >
                  {MONTH_NAMES.map((n, i) => <option key={n} value={i + 1}>{n}</option>)}
                </select>
              </label>
            </div>
            <label className="block mt-3">
              <span className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">Note (optional)</span>
              <input
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder="e.g. Reviewed by Priya on 03 Feb"
                data-testid="lock-period-note"
                className="mt-1 w-full px-3 py-2 border text-sm"
                style={{ borderColor: "var(--ll-border)" }}
              />
            </label>
            <div className="pt-4 flex items-center justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 border text-sm" style={{ borderColor: "var(--ll-border)" }}>Cancel</button>
              <button
                type="submit"
                disabled={busy}
                data-testid="lock-period-submit"
                className="px-4 py-2 bg-[var(--ll-ink)] text-white hover:bg-[var(--ll-ink-hover)] text-sm disabled:opacity-50 flex items-center gap-1.5"
              >
                {busy && <Loader2 size={12} className="animate-spin" />} Close month
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
